import React, { type ReactNode } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { LayoutGroup, Reorder } from 'framer-motion'
import { useDebounceCallback } from 'usehooks-ts'
import { v4 as uuid } from 'uuid'

import { useForm } from 'modules/core/hooks/useForm'
import i18n from 'modules/core/lib/i18n'

import Question from 'modules/questionnaires/components/Question'
import QuestionIcon from 'modules/questionnaires/components/QuestionIcon'
import { handleFormSubmit } from 'modules/questionnaires/lib/formHelpers'
import { getQuestionTypeSelector } from 'modules/questionnaires/lib/helpers'
import {
  QuestionTypeSelector,
  Question as QuestionValue,
  Section as SectionType,
  Translations,
} from 'modules/questionnaires/lib/types'

import DeleteSectionModal from './DeleteSectionModal'
import SectionButton from './SectionButton'
import SectionHeader from './SectionHeader'

type SectionValue = Omit<SectionType, 'locked'>

type Handler = (question: SectionValue) => Promise<SectionValue> | void
type Props = {
  section?: SectionValue
  editing?: boolean
  collapsed?: boolean
  disabled?: boolean
  locked?: boolean
  hideLockedIcon?: boolean
  hideQuestionTypes?: boolean
  translations: Pick<Translations, 'section' | 'question'>
  formType?: string
  renderHeaderAddon?: (sectionId: string) => ReactNode | undefined
  onSave?: Handler
  onDuplicate?: Handler
  onDelete?: Handler
  onSaveAndDuplicate?: (questions: SectionValue[]) => Promise<SectionValue[]> | void
}

const Section: React.FC<Props> = ({
  section,
  editing = false,
  collapsed = false,
  disabled = false,
  locked = false,
  hideLockedIcon = false,
  hideQuestionTypes = false,
  renderHeaderAddon,
  formType,
  translations,
  onSave,
  onDuplicate,
  onDelete,
  onSaveAndDuplicate,
}) => {
  const [showQuestionForm, toggleQuestionForm] = React.useState(false)
  const [showDeleteQuestionModal, toggleDeleteQuestionModal] = React.useState(false)
  const [isEditing, toggleEditing] = React.useState(editing)
  const [lastDuplicated, setLastDuplicated] = React.useState<string | undefined>(undefined)
  const [open, toggle] = React.useState(!collapsed)
  const form = useForm<SectionValue>({
    defaultValues: {
      uuid: section?.uuid || uuid(),
      title: section?.title || '',
      questions: section?.questions || [],
    },
    onSubmit: (data) => {
      if (!form.dirty || !onSave) {
        return handleFormSubmit(() => {})
      }

      return handleFormSubmit(onSave, data)
    },
  })

  const debouncedSubmit = useDebounceCallback(form.submitForm, 0)

  const questionTypes: QuestionTypeSelector[] = Array.from(
    new Set((form.values.questions || []).map((q) => getQuestionTypeSelector(q)))
  )
  const showQuestionTypes = !!questionTypes.length && !open && !hideQuestionTypes

  const handleTitleEdition = (title: string) => {
    form.setFieldValue('title', title)
    toggleEditing(false)
    debouncedSubmit()
  }

  const handleQuestionsChange = (questions: QuestionValue[]) => {
    form.setFieldValue('questions', questions)
    debouncedSubmit()
  }

  const handleAddQuestion = (question: QuestionValue) => {
    handleQuestionsChange([...form.values.questions, question])
    toggleQuestionForm(false)
  }

  const handleUpdateQuestion = (question: QuestionValue) => {
    const updatedQuestions = form.values.questions.map((q) =>
      q.uuid === question.uuid ? question : q
    )
    handleQuestionsChange(updatedQuestions)
  }

  const handleDuplicateQuestion = (question: QuestionValue) => {
    handleQuestionsChange([...form.values.questions, question])
    toggleQuestionForm(false)
    setLastDuplicated(question.uuid)
  }

  const handleSaveAndDuplicateQuestions = (questions: QuestionValue[]) => {
    handleQuestionsChange([...form.values.questions, ...questions])
    toggleQuestionForm(false)
    setLastDuplicated(questions[questions.length - 1]?.uuid)
  }

  const handleDeleteQuestion = (question: QuestionValue) => {
    const updatedQuestions = form.values.questions.filter((q) => q.uuid !== question.uuid)
    handleQuestionsChange(updatedQuestions)
  }

  const handleQuestionReorder = (reorderedQuestions: QuestionValue[]) => {
    handleQuestionsChange(reorderedQuestions)
  }

  const handleDeleteSection = () => {
    if (form.values.questions.length > 0) {
      toggleDeleteQuestionModal(true)
      return
    }

    onDelete?.(form.values)
  }

  const handleDuplicateSection = () => {
    const duplicatedQuestions = form.values.questions.map((q) => ({ ...q, uuid: uuid() }))
    const duplicatedSection = {
      ...form.values,
      ...{
        uuid: uuid(),
        title: `${form.values.title} ${translations.section.title.copy}`,
        questions: duplicatedQuestions,
      },
    }

    if (section === undefined && onSaveAndDuplicate) {
      onSaveAndDuplicate([form.values, duplicatedSection])
      return
    }

    onDuplicate?.(duplicatedSection)
    toggleEditing(false)
  }

  return (
    <Box
      gap="s16"
      paddingY="s24"
      paddingRight="s40"
      background="grey100"
      borderRadius={{ all: 'abs016' }}
      aria-labelledby={
        editing
          ? `section-${form.values.uuid}-input-title`
          : `section-${form.values.uuid}-display-title`
      }
      role="region"
    >
      <SectionHeader
        id={form.values.uuid}
        title={form.values.title}
        formType={formType}
        locked={locked}
        renderHeaderAddon={renderHeaderAddon}
        hideLockedIcon={hideLockedIcon}
        translations={translations.section}
        editing={isEditing}
        disabled={disabled}
        onCollapse={() => toggle(!open)}
        collapsed={!open}
        onSave={handleTitleEdition}
        placeholder={translations.section.title.placeholder}
        options={[
          {
            title: translations.section.default_actions.edit,
            icon: 'pencil',
            onClick: () => toggleEditing(true),
            disabled: disabled || isEditing,
          },
          {
            title: translations.section.default_actions.duplicate,
            icon: 'duplicate',
            onClick: () => handleDuplicateSection(),
            disabled:
              (section === undefined && !onSaveAndDuplicate) || disabled || !form.values.title,
          },
          {
            title: translations.section.default_actions.delete,
            icon: 'delete',
            danger: true,
            disabled: disabled,
            onClick: () => handleDeleteSection(),
          },
        ]}
      />
      {showQuestionTypes && (
        <Box
          paddingLeft="s40"
          flexDirection="row"
          gap="s16"
          alignItems="center"
          onClick={() => toggle(!open)}
        >
          <Text.Normal>
            {translations.section.questions_count({
              count: form.values.questions.length,
            })}
          </Text.Normal>
          <Box flexDirection="row" gap="s8" alignItems="center">
            {questionTypes.map((type) => (
              <QuestionIcon key={`section-${form.values.uuid}-${type}`} type={type} />
            ))}
          </Box>
        </Box>
      )}

      {open && (
        <Box paddingLeft="s40" gap="s16">
          {!locked && (
            <Reorder.Group
              values={form.values.questions}
              onReorder={handleQuestionReorder}
              initial={false}
              axis="y"
            >
              <Box gap="s16">
                <LayoutGroup>
                  {form.values.questions.map((question) => (
                    <Reorder.Item
                      id={question.uuid}
                      key={question.uuid}
                      value={question}
                      initial={false}
                    >
                      <Question
                        question={question}
                        disabled={disabled}
                        editing={lastDuplicated === question.uuid}
                        translations={translations.question}
                        onSave={handleUpdateQuestion}
                        onDuplicate={handleDuplicateQuestion}
                        onDelete={handleDeleteQuestion}
                      />
                    </Reorder.Item>
                  ))}
                </LayoutGroup>
              </Box>
            </Reorder.Group>
          )}

          {locked &&
            form.values.questions.map((question) => (
              <Question
                key={question.uuid}
                question={question}
                translations={translations.question}
                locked
              />
            ))}

          {showQuestionForm && (
            <Question
              editing
              disabled={disabled}
              translations={translations.question}
              onSave={handleAddQuestion}
              onDelete={() => toggleQuestionForm(false)}
              onDuplicate={handleDuplicateQuestion}
              onSaveAndDuplicate={handleSaveAndDuplicateQuestions}
            />
          )}

          {!locked && (
            <SectionButton
              icon="add"
              onClick={() => toggleQuestionForm(true)}
              disabled={!form.values.title || disabled}
              ariaLabel={
                section
                  ? i18n.t(
                      'performance_process.reviews.questionnaires_by_strategy.section.add_question_aria_label',
                      { section_title: section.title }
                    )
                  : undefined
              }
            >
              {translations.section.add_question}
            </SectionButton>
          )}
        </Box>
      )}

      {showDeleteQuestionModal && (
        <DeleteSectionModal
          translations={translations.section.delete_modal}
          onDelete={() => {
            onDelete?.(form.values)
            toggleDeleteQuestionModal(false)
          }}
          onClose={() => toggleDeleteQuestionModal(false)}
        />
      )}
    </Box>
  )
}

export default Section
